import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  CardContent,
} from "@mui/material";
import "../../css/style.css";
import Base from "../../../Base/views/Base";
import VisitorChartWithPagination from "../../../VisitorsManagement/views/Dashboard/BarChartPagination";
import {
  GetPayrollApveEmployeeHelper,
  GetPayrollIncentiveHelper,
  GetPayrollSalaryPayoutHelper,
} from "../../helper/Dashobard/PayrollDashboardHelper";
import { Fullscreen, FullscreenExit } from "@mui/icons-material";
import avg_payment from "../../images/avg_payment.png";
import calendar from "../../images/calendar.png";
import public_safety from "../../images/public-safety.png";
import BackdropMeepl from "../../../Ancillary/AnimationComponents/BackdropMeepl";

const monthMap = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

const extractChartData = (dataArray, valueKey) => {
  if (!dataArray || dataArray?.length === 0) {
    return { months: [], values: [] };
  }
  const months = dataArray?.map((entry) => monthMap[entry.month] || "");
  const values = dataArray?.map((entry) => Number(entry[valueKey] || 0));
  return { months, values };
};
const PayrollDashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [salaryPayout, setSalaryPayout] = useState([]);
  const [salaryApve, setSalaryApve] = useState([]);
  const [salaryIncentive, setSalaryIncentive] = useState([]);
  const [expandApve, setExpandApve] = useState(false);
  const [expandPayout, setExpandPayout] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [salaryRes, apveRes, incentRes] = await Promise.all([
          GetPayrollSalaryPayoutHelper(),
          GetPayrollApveEmployeeHelper(),
          GetPayrollIncentiveHelper(),
        ]);
        if (salaryRes?.data?.statuscode === 200) {
          setSalaryPayout(salaryRes?.data?.data);
        }
        if (apveRes?.data?.statuscode === 200) {
          setSalaryApve(apveRes?.data?.data);
        }
        if (incentRes?.data?.statuscode === 200) {
          setSalaryIncentive(incentRes?.data?.data);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const { months: salaryMonths, values: salaryValues } = extractChartData(
    salaryPayout,
    "payout"
  );
  const { months: apveMonths, values: apveValues } = extractChartData(
    salaryApve,
    "apve"
  );
  const { months: incentiveMonths, values: incentiveValues } = extractChartData(
    salaryIncentive,
    "incentive"
  );
  const salaryChartData = {
    series: salaryValues.length
      ? [{ name: "Salary Payout", data: salaryValues }]
      : [],
    options: {
      chart: {
        id: "salary-bar",
        type: "bar",
        toolbar: {
          show: false,
        },
      },
      xaxis: { categories: salaryMonths },
      dataLabels: { enabled: true },
      colors: ["#76B7B2"],
      yaxis: {
        title: {
          text: "Amount",
        },
      },
    },
  };

  const apveChartData = {
    type: "line",
    series: apveValues.length ? [{ name: "APVE", data: apveValues }] : [],
    options: {
      chart: {
        id: "apve-line",
        type: "line",
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: true,
        },
        toolbar: {
          show: true,
          tools: {
            download: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      },
      xaxis: { categories: apveMonths },
      yaxis: {
        title: {
          text: "Amount",
        },
      },
      colors: ["#008FFB"],
      stroke: { curve: "smooth" },
      dataLabels: { enabled: true },
      markers: { size: 5 },
    },
  };

  const incentiveChartData = {
    series: incentiveValues.length
      ? [{ name: "Incentives Payout", data: incentiveValues }]
      : [],
    options: {
      chart: {
        id: "incentive-bar",
        type: "bar",
        toolbar: {
          show: false,
        },
      },
      xaxis: { categories: incentiveMonths },
      yaxis: {
        title: {
          text: "Amount",
        },
      },
      colors: ["#F28E2B"],
    },
  };
  const showIncentiveBarChart = salaryIncentive?.length === 0;
  return (
    <>
      <Base>
        <Box
          sx={{
            height: "calc(100vh - 65px)",
            overflowY: "auto",
            position: "relative",
            pt: 2,
            pb: 3,
            pl: 1,
            pr: 1,
          }}
        >
          <BackdropMeepl open={isLoading} dark={false}/>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={1}
          >
            <Grid item xs={4}>
              <Card sx={{ display: "block", overflow: "visible", height: 100 }}>
                <CardContent sx={{ padding: "12px !important" }}>
                  <Box
                    display="flex"
                    justifyContent={"space-between"}
                    height={"100%"}
                  >
                    <Typography
                      sx={{ fontSize: 14 }}
                      variant="subtitle2"
                      color="primary.contrastText"
                      gutterBottom
                    >
                      Payroll Employee Count
                    </Typography>
                  </Box>
                  <Box 
                    display="flex"
                    justifyContent={"space-between"}
                    alignItems="center">
                      <Box>
                        <Typography variant="h5" gutterBottom>
                          {salaryApve?.length
                            ? salaryApve?.[salaryApve?.length - 1]?.activeemployees
                            : 0}
                        </Typography>
                        <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={"5px"}
                            height={"15px"}
                          >
                            <Typography variant="body2" gutterBottom>
                              As per last 6 months data
                            </Typography>
                          </Box>
                      </Box>
                    <img
                      src={public_safety}
                      alt="Ratings"
                      className="card-icon"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ display: "block", overflow: "visible", height: 100 }}>
                <CardContent sx={{ padding: "12px !important" }}>
                  <Box
                    display="flex"
                    justifyContent={"space-between"}
                    height={"100%"}
                  >
                    <Typography
                      sx={{ fontSize: 14 }}
                      variant="subtitle2"
                      color="primary.contrastText"
                      gutterBottom
                    >
                      Salary Payout
                    </Typography>
                  </Box>
                  <Box justifyContent={"space-between"} display="flex">
                    <Typography textAlign="center" variant="h5" gutterBottom>
                      {salaryMonths?.length && salaryValues?.length
                        ? `${salaryMonths[salaryMonths.length - 1]}: ₹${
                            salaryValues[salaryValues.length - 1]
                          }`
                        : 0}
                    </Typography>
                    <img src={calendar} alt="Ratings" className="card-icon" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ display: "block", overflow: "visible", height: 100 }}>
                <CardContent sx={{ padding: "12px !important" }}>
                  <Box
                    display="flex"
                    justifyContent={"space-between"}
                    height={"100%"}
                  >
                    <Typography
                      sx={{ fontSize: 14 }}
                      variant="subtitle2"
                      color="primary.contrastText"
                      gutterBottom
                    >
                      APVE
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent={"space-between"}
                    alignItems="center"
                  >
                    <Box>
                      {apveValues?.length >= 2 ? (
                        <>
                          <Typography variant="h5" gutterBottom>
                            ₹{apveValues[apveValues.length - 1]}
                          </Typography>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={"5px"}
                            height={"15px"}
                          >
                            <Typography variant="body2" gutterBottom>
                              vs last month
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              sx={{
                                color:
                                  apveValues[apveValues.length - 1] >=
                                  apveValues[apveValues.length - 2]
                                    ? "green"
                                    : "red",
                              }}
                            >
                              {(() => {
                                const latest =
                                  apveValues[apveValues.length - 1];
                                const prev = apveValues[apveValues.length - 2];
                                const diff = latest - prev;
                                const percent = Math.abs(
                                  (diff / prev) * 100
                                ).toFixed(2);
                                return `${percent}%`;
                              })()}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <Typography variant="h5" gutterBottom>
                          ₹{apveValues?.[apveValues.length - 1] || 0}
                        </Typography>
                      )}
                    </Box>
                    <img
                      src={avg_payment}
                      alt="Ratings"
                      className="card-icon"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Grid
            mt={2}
            container
            display="flex"
            direction="row"
            justifyContent="start"
            alignItems="start"
            spacing={1}
          >
            <Grid
              item
              xs={12}
              md={expandPayout ? 12 : 6}
              sx={{ height: "510px", backgroundColor: "#ffffff" }}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#ffffff",
                }}
              >
                <CardHeader
                  sx={{ backgroundColor: "#ffffff" }}
                  title={
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="h6">
                        {"Salary Payout by Month"}
                      </Typography>
                      <Tooltip
                        title={expandPayout ? "Collapse Chart" : "Expand Chart"}
                      >
                        <IconButton
                          onClick={() => setExpandPayout(!expandPayout)}
                        >
                          {expandPayout ? <FullscreenExit /> : <Fullscreen />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
                <Box sx={{ flexGrow: 1, backgroundColor: "#ffffff" }}>
                  <VisitorChartWithPagination
                    chartData={salaryChartData}
                    showPagination={false}
                  />
                </Box>
              </Card>
            </Grid>
            <Grid
              item
              xs={12}
              md={expandApve ? 12 : 6}
              sx={{ height: "510px", backgroundColor: "#ffffff" }}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#ffffff",
                }}
              >
                <CardHeader
                  sx={{ backgroundColor: "#ffffff" }}
                  title={
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box display="flex" alignItems="center" gap={"15px"}>
                        <Typography variant="h6">APVE by Month</Typography>
                        <Typography variant="body2" color="inherit">
                          (Average Payroll Value per Employee)
                        </Typography>
                      </Box>
                      <Tooltip
                        title={expandApve ? "Collapse Chart" : "Expand Chart"}
                      >
                        <IconButton onClick={() => setExpandApve(!expandApve)}>
                          {expandApve ? <FullscreenExit /> : <Fullscreen />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
                <Box sx={{ flexGrow: 1, backgroundColor: "#ffffff" }}>
                  <VisitorChartWithPagination
                    chartData={{
                      ...apveChartData,
                      type: "line",
                    }}
                    showPagination={false}
                  />
                </Box>
              </Card>
            </Grid>

           { !showIncentiveBarChart &&( <Grid
              item
              xs={12}
              md={12}
              sx={{ height: "510px", backgroundColor: "#ffffff" }}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#ffffff",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Box sx={{ width: {xs: "100%", md: "50%"} }}>
                    <CardHeader
                      sx={{ backgroundColor: "#ffffff" }}
                      title={
                        <Typography variant="h6">
                          Incentive Payout by Month
                        </Typography>
                      }
                    />

                    <Box>
                      <VisitorChartWithPagination
                        chartData={incentiveChartData}
                        showPagination={false}
                      />
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>)}
          </Grid>
        </Box>
      </Base>
    </>
  );
};

export default PayrollDashboard;
